import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { getClaimsFilters, resetClaimsFilter, setClaimsFilter } from 'domains/order/Order.store';
import {
  ClaimsFilterAndSortFieldLocal,
  ClaimsFilterFields,
  ClaimStatus,
  DATE_FROM,
  DATE_TO,
  ZCMR_ZGA2,
} from 'domains/order/Order.types';
import { getReasons } from 'pages/MyOrdersPage/OrderClaims/claimFormData';
import { handleRangeFilterChange } from 'pages/MyOrdersPage/orders.utils';
import { theme } from 'styles';
import { Box, Flex, Input, MarginBox, Select, Text } from 'UI';
import RangePicker from 'UI/DatePicker/RangePicker';

export const FlexWrapper = styled(Flex)`
  input,
  .ant-select-selector,
  .ant-select ant-select-single,
  .ant-select-allow-clear,
  .ant-select-show-arrow,
  .ant-input-affix-wrapper {
    border: solid 1px ${theme.color.grey55};
    border-radius: 4px;
  }
`;

const InputField = ({
  placeholder,
  onChangeDebounced,
  value,
}: {
  placeholder: string;
  value?: string;
  onChangeDebounced: (value: string) => void;
}) => {
  const [localValue, setLocalValue] = useState<string | undefined>(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <WidthWrapper>
      <Input
        onChangeDebounced={onChangeDebounced}
        placeholder={placeholder}
        bordered
        value={localValue}
        onChange={setLocalValue}
      />
    </WidthWrapper>
  );
};

const WidthWrapper = ({ width = 150, children }: { width?: number; children: React.ReactNode }) => (
  <Box width={width}>{children}</Box>
);

const ClaimsFilter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector(getClaimsFilters);

  const updateFilters = (field: ClaimsFilterAndSortFieldLocal, value: string | string[]) => {
    dispatch(setClaimsFilter({ field, value }));
  };

  const getStatusValue = () => {
    const filterStatus = filters.get(ClaimsFilterFields.STATUS);
    if (Array.isArray(filterStatus)) {
      return ClaimStatus.IN_PROGRESS_OK;
    }
    return filterStatus;
  };

  const getDocumentTypeValue = () => {
    const filterType = filters.get(ClaimsFilterFields.LAST_DOCUMENT_TYPE);
    if (Array.isArray(filterType)) {
      return ZCMR_ZGA2;
    }
    return filterType;
  };

  return (
    <FlexWrapper direction={'column'} gap={30}>
      <Flex align={'center'} gap={30}>
        <Text type="heading4" disableGutter>
          {t('common.filters', 'Filters')}
        </Text>
        <MarginBox mt={3}>
          <Text
            type="heading5"
            color="white"
            displayStyle={'link'}
            disableGutter
            hoverUnderLine
            cursor={'pointer'}
            onClick={() => {
              dispatch(resetClaimsFilter());
            }}
          >
            {t('common.action.reset_filters', 'Reset filters')}
          </Text>
        </MarginBox>
      </Flex>
      <Flex gap={20} wrap={'wrap'}>
        <InputField
          placeholder={t('order.claim.claim_id', 'Claim #')}
          onChangeDebounced={(value) => updateFilters(ClaimsFilterFields.RP_4_CLAIM_ID, value)}
          value={filters.get(ClaimsFilterFields.RP_4_CLAIM_ID) as string}
        />
        <WidthWrapper>
          <Select
            dropdownMatchSelectWidth={false}
            onChange={(value: string) => {
              const valueMap: Record<string, string | string[]> = {
                ZCMR_ZGA2: ['ZCMR', 'ZGA2'],
                ZDMR: 'ZDMR',
                ZACR: 'ZACR',
                ZRVD: 'ZRVD',
              };
              const realValues = valueMap[value];
              updateFilters(
                ClaimsFilterFields.LAST_DOCUMENT_TYPE,
                filters.get(ClaimsFilterFields.LAST_DOCUMENT_TYPE) === realValues ? '' : realValues,
              );
            }}
            allowClear
            styleLess
            value={getDocumentTypeValue()}
            placeholder={t('order.claim.type', 'Type')}
            options={[
              {
                title: t('order.claim.type.credit_memo_request', 'Credit Memo Request'),
                value: 'ZCMR_ZGA2',
              },
              {
                title: t('order.claim.type.debit_memo_request', 'Debit Memo Request'),
                value: 'ZDMR',
              },
              {
                title: t('order.claim.type.advance_return_order', 'Advance Return Order'),
                value: 'ZACR',
              },
              {
                title: t('order.claim.type.return_notif_vendor', 'Return notif vendor'),
                value: 'ZRVD',
              },
            ]}
            bordered
          />
        </WidthWrapper>
        <InputField
          placeholder={t('order.claim.order_id', 'Order #')}
          onChangeDebounced={(value) => updateFilters(ClaimsFilterFields.R_1_ORDER_ID, value)}
          value={filters.get(ClaimsFilterFields.R_1_ORDER_ID) as string}
        />
        <InputField
          placeholder={t('order.claim.supplier_credit_debit_note_id', 'Credit/Debit note #')}
          onChangeDebounced={(value) => updateFilters(ClaimsFilterFields.NOTE_ID, value)}
          value={filters.get(ClaimsFilterFields.NOTE_ID) as string}
        />
        <WidthWrapper width={250}>
          <RangePicker
            onChange={(dateStrings) => handleRangeFilterChange(dateStrings, updateFilters)}
            value={[filters.get(DATE_FROM) as string, filters.get(DATE_TO) as string]}
          />
        </WidthWrapper>
        <WidthWrapper>
          <Select
            onChange={(value: string) => {
              updateFilters(ClaimsFilterFields.CODE, filters.get(ClaimsFilterFields.CODE) === value ? '' : value);
            }}
            allowClear
            styleLess
            placeholder={t('order.claim.reason', 'Reason')}
            options={getReasons(t)}
            value={filters.get(ClaimsFilterFields.CODE) as string}
            bordered
            dropdownMatchSelectWidth={false}
          />
        </WidthWrapper>
        <InputField
          placeholder={t('order.claim.reference', 'Reference')}
          onChangeDebounced={(value) => updateFilters(ClaimsFilterFields.REFERENCE, value)}
          value={filters.get(ClaimsFilterFields.REFERENCE) as string}
        />
        <InputField
          placeholder={t('order.claim.designation', 'Designation')}
          onChangeDebounced={(value) => updateFilters(ClaimsFilterFields.REFERENCE_NAME, value)}
          value={filters.get(ClaimsFilterFields.REFERENCE_NAME) as string}
        />
        <WidthWrapper>
          <Select
            dropdownMatchSelectWidth={false}
            onChange={(value: string) => {
              const valueMap: Record<string, string | string[]> = {
                IN_PROGRESS_OK: [ClaimStatus.IN_PROGRESS, ClaimStatus.OK],
                ACCEPTED: ClaimStatus.ACCEPTED,
                REFUSED: ClaimStatus.REFUSED,
              };
              const realValues = valueMap[value];
              updateFilters(
                ClaimsFilterFields.STATUS,
                filters.get(ClaimsFilterFields.STATUS) === realValues ? '' : realValues,
              );
            }}
            allowClear
            styleLess
            placeholder={t('order.claim.status', 'Status')}
            value={getStatusValue()}
            options={[
              {
                title: t('order.claim.status.in_progress', 'In progress'),
                value: ClaimStatus.IN_PROGRESS_OK,
              },
              {
                title: t('order.claim.status.accepted', 'Accepted'),
                value: ClaimStatus.ACCEPTED,
              },
              {
                title: t('order.claim.status.rejected', 'Rejected'),
                value: ClaimStatus.REFUSED,
              },
            ]}
            bordered
          />
        </WidthWrapper>
      </Flex>
    </FlexWrapper>
  );
};

export default ClaimsFilter;
